/**
 * Yandex.Metrika Integration
 * 
 * Инициализируется только в prod среде.
 * Counter ID: 106332205
 * 
 * @see https://metrika.yandex.ru/dashboard?id=106332205
 */

import { isProd } from '@/lib/config/environment';

const COUNTER_ID = 106332205;

declare global {
  interface Window {
    ym?: ((counterId: number, action: string, ...args: unknown[]) => void) & {
      a?: unknown[];
      l?: number;
    };
  }
}

/**
 * Initialize Yandex.Metrika counter
 * Only runs in production environment
 */
export function initYandexMetrika(): void {
  // Skip if not production
  if (!isProd) {
    console.log('[YandexMetrika] Skipped: not production environment');
    return;
  }

  // Skip if already initialized
  if (window.ym) {
    console.log('[YandexMetrika] Already initialized');
    return;
  }

  try {
    // Initialize ym function
    (function(m: Window, e: Document, t: string, r: string, i: 'ym') {
      const ymFunc = function(...args: unknown[]) {
        (ymFunc.a = ymFunc.a || []).push(args);
      } as Window['ym'];
      ymFunc.l = Date.now();
      m[i] = ymFunc;
      
      // Check if script already loaded
      for (let j = 0; j < e.scripts.length; j++) {
        if (e.scripts[j].src === r) return;
      }
      
      const k = e.createElement(t) as HTMLScriptElement;
      const a = e.getElementsByTagName(t)[0];
      k.async = true;
      k.src = r;
      a.parentNode?.insertBefore(k, a);
    })(
      window,
      document,
      'script',
      `https://mc.yandex.ru/metrika/tag.js?id=${COUNTER_ID}`,
      'ym'
    );

    // Initialize counter with settings
    window.ym?.(COUNTER_ID, 'init', {
      ssr: true,
      webvisor: true,
      clickmap: true,
      ecommerce: 'dataLayer',
      accurateTrackBounce: true,
      trackLinks: true,
    });

    // Add noscript fallback
    addNoscriptFallback();

    console.log('[YandexMetrika] Initialized successfully');
  } catch (error) {
    console.warn('[YandexMetrika] Failed to initialize:', error);
  }
}

/**
 * Add noscript fallback image
 */
function addNoscriptFallback(): void {
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<div><img src="https://mc.yandex.ru/watch/${COUNTER_ID}" style="position:absolute;left:-9999px;" alt="" /></div>`;
  document.body.appendChild(noscript);
}

/**
 * Track page view (for SPA navigation)
 */
export function trackPageView(url?: string): void {
  if (!isProd || !window.ym) return;
  
  window.ym(COUNTER_ID, 'hit', url || window.location.href);
}

/**
 * Track custom goal
 */
export function reachGoal(goalName: string, params?: Record<string, unknown>): void {
  if (!isProd || !window.ym) return;
  
  window.ym(COUNTER_ID, 'reachGoal', goalName, params);
}

export default {
  init: initYandexMetrika,
  trackPageView,
  reachGoal,
};
